import { Link } from "react-router-dom";
import {
  ArrowRight,
  Landmark,
  Building2,
  HeartPulse,
  Home as HomeIcon,
  Compass,
  Stethoscope,
  Briefcase,
  Globe2,
} from "lucide-react";
import FadeIn from "@/components/FadeIn";
import SeoHead from "@/components/SeoHead";
import InternalLinkBlock from "@/components/InternalLinkBlock";
import { SECTORS } from "@/data/sectors";

const audiences = [
  {
    icon: Briefcase,
    title: "Bedrijven en MKB",
    text:
      "Voor bedrijven en MKB-organisaties die AI gebruiken in klantcontact, HR, software, planning, marketing, analyse, risicobeoordeling of interne processen. SovEU helpt om AI-gebruik, risico's, documentatie en governance helder in kaart te brengen richting AI Act, AVG en interne beheersing. Denk aan AI Act MKB-vraagstukken, AI-governance voor bedrijven, ChatGPT-beleid en Microsoft Copilot governance.",
    cta: { label: "Bekijk diensten voor bedrijven", to: "/diensten#ai-act-startcheck" },
  },
  {
    icon: Compass,
    title: "Adviesbureaus en dienstverleners",
    text:
      "Voor privacy-, IT-, HR-, compliance- en organisatieadviesbureaus die klanten krijgen met vragen over AI Act, AI-geletterdheid, AI-registers, AI-beleid en verantwoord gebruik van generatieve AI. SovEU kan ondersteunen als specialistische partner of white-label uitvoerder.",
    cta: { label: "Bekijk white-label AI Act ondersteuning", to: "/diensten#white-label" },
  },
  {
    icon: Landmark,
    title: "Gemeenten en publieke organisaties",
    text:
      "Voor gemeenten, uitvoeringsorganisaties en publieke samenwerkingsverbanden die bestuurlijke grip willen op algoritmen, AI-systemen, datastromen en digitale afhankelijkheden.",
    cta: { label: "Bekijk AI-governance voor publieke organisaties", to: "/diensten" },
  },
  {
    icon: HomeIcon,
    title: "Woningcorporaties, onderwijs en zorg",
    text:
      "Voor semipublieke organisaties waar AI, data en digitale systemen direct raken aan bewoners, studenten, cliënten, medewerkers en maatschappelijke verantwoording.",
    cta: { label: "Plan een kennismaking", to: "/contact" },
  },
  {
    icon: HeartPulse,
    title: "GGD'en en veiligheidsregio's",
    text:
      "Gevoelige persoonsgegevens, crisisprocessen, informatiebeveiliging en bestuurlijke verantwoording. Juist in organisaties waar snelheid en zorgvuldigheid samen moeten komen, is bestuurlijk inzicht in AI gebruik en cloudpositie essentieel.",
  },
  {
    icon: Stethoscope,
    title: "Zorg en maatschappelijke organisaties",
    text:
      "Cliëntgegevens, gevoelige dossiers, AI ondersteuning, cloudafhankelijkheid en toezicht. Wij vertalen technische vraagstukken naar bestuurlijke keuzes die passen bij de aard van de organisatie en de mensen die zij bedient.",
  },
  {
    icon: Building2,
    title: "Besturen en directies",
    text:
      "Bestuurlijke rust, aantoonbare controle, prioriteiten en besluitvorming. Voor wie in de eigen organisatie eindverantwoordelijk is voor digitale risico's, ook als de techniek elders is belegd.",
  },
  {
    icon: Globe2,
    title: "Organisaties die kiezen voor digitale regie",
    text:
      "Voor organisaties die bewuster willen omgaan met digitale afhankelijkheid. In steeds meer categorieën, zoals cloudopslag, e mail, videoconferencing, documentbeheer en AI toepassingen, bestaan serieuze Europese alternatieven. SovEU helpt organisaties deze opties bestuurlijk te beoordelen: wat levert meer zeggenschap op, wat is uitvoerbaar en welke keuze past bij risico, kosten en continuïteit?",
  },
];

const sharedTraits = [
  "Verwerken gevoelige data van burgers, klanten, huurders, studenten, cliënten of medewerkers",
  "Moeten besluiten kunnen uitleggen aan toezichthouders, raden, directie of media",
  "Werken met externe leveranciers en cloudservices voor primaire processen",
  "Hebben bestuurlijke, maatschappelijke of zakelijke verantwoordelijkheid die verder gaat dan technische compliance",
];

export default function VoorWie() {
  return (
    <div data-testid="page-voor-wie" className="pt-[96px]">
      <SeoHead
        title="Voor wie werkt SovEU? Bedrijven, adviesbureaus en publieke organisaties"
        description="SovEU werkt voor bedrijven, adviesbureaus, gemeenten, woningcorporaties, onderwijs en zorg rond AI Act readiness, AI-governance en digitale regie."
        path="/voor-wie"
      />
      {/* Hero */}
      <section className="section bg-white">
        <div className="container-soveu">
          <FadeIn className="max-w-3xl">
            <div className="eyebrow mb-4">Doelgroepen</div>
            <h1 className="h1-display mb-6">Voor wie SovEU werkt</h1>
            <div className="space-y-4 body-lg text-[#334155] leading-relaxed">
              <p>
                Wij werken voor publieke organisaties, semipublieke organisaties en bedrijven die AI, data, algoritmen en digitale systemen verantwoord willen gebruiken. Denk aan gemeenten, woningcorporaties, onderwijs- en zorgorganisaties, MKB-bedrijven, IT-dienstverleners, privacy- en compliancebureaus en andere adviesorganisaties.
              </p>
              <p>
                De context verschilt per organisatie. De kernvraag is vaak dezelfde: wat gebruiken we, welke risico's spelen er, wat kunnen we aantonen en welke vervolgstappen zijn nodig?
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Audience grid */}
      <section
        className="section"
        style={{ backgroundColor: "#F5F7FA" }}
        data-testid="voor-wie-grid"
      >
        <div className="container-soveu">
          <FadeIn className="max-w-3xl mb-12">
            <h2 className="h2-display mb-4">Onze typische opdrachtgevers</h2>
            <p className="text-[#334155] body-lg">
              Hieronder een overzicht van de organisaties waarvoor SovEU het meest werkt. Herkent u uw organisatie hier niet, maar speelt het thema wel? Plan dan een vrijblijvend gesprek — wij verkennen graag of onze aanpak past.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-6">
            {audiences.map((a, i) => (
              <FadeIn key={a.title} delay={i * 60}>
                <article
                  className="soveu-card p-7 h-full"
                  data-testid={`audience-${i + 1}`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="w-11 h-11 rounded-md inline-flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: "#F5F7FA", color: "#1E3A5F" }}
                    >
                      <a.icon size={22} strokeWidth={1.75} />
                    </div>
                    <div className="flex-1">
                      <h3 className="h3-display mb-3">{a.title}</h3>
                      <p className="text-[#334155] leading-relaxed text-[15px]">
                        {a.text}
                      </p>
                      {a.cta && (
                        <Link
                          to={a.cta.to}
                          className="inline-flex items-center gap-1.5 text-[14px] font-medium mt-4 hover:underline underline-offset-4 transition-colors"
                          style={{ color: "#2D6F8F" }}
                          data-testid={`audience-cta-${i + 1}`}
                        >
                          {a.cta.label}
                          <ArrowRight size={15} />
                        </Link>
                      )}
                    </div>
                  </div>
                </article>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Sectoren — interne links naar sectorpagina's */}
      <InternalLinkBlock
        eyebrow="Sectoren"
        title="Sectorpagina's"
        intro="Per sector werken wij uit welke vraagstukken rond AI governance, datagovernance, AVG praktijk en digitale afhankelijkheid het meest spelen."
        items={SECTORS.map((s) => ({
          to: s.to,
          label: s.h1,
          summary: s.summary,
        }))}
        columns={2}
        testid="voor-wie-sectoren"
      />

      {/* Shared traits */}
      <section className="section bg-white" data-testid="voor-wie-shared">
        <div className="container-soveu max-w-4xl">
          <FadeIn className="mb-10">
            <div className="eyebrow mb-3">Wat hebben deze organisaties gemeen</div>
            <h2 className="h2-display mb-5">
              Eén bestuurlijke vraag, verschillende organisaties
            </h2>
            <p className="body-lg text-[#334155]">
              De sectoren verschillen, maar de vraag erachter is steeds dezelfde: kunnen wij uitleggen wat onze digitale systemen doen, welke data zij verwerken en welke keuzes wij daarin maken? Onze opdrachtgevers delen vier kenmerken.
            </p>
          </FadeIn>

          <ul className="space-y-3">
            {sharedTraits.map((trait, i) => (
              <FadeIn key={trait} delay={i * 80}>
                <li
                  className="soveu-card flex items-start gap-4 p-5"
                  data-testid={`shared-trait-${i + 1}`}
                >
                  <span
                    className="flex-shrink-0 w-8 h-8 rounded-full inline-flex items-center justify-center text-[14px] font-semibold"
                    style={{ backgroundColor: "#F5F7FA", color: "#1E3A5F" }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[#334155] text-[15px] leading-relaxed pt-1">
                    {trait}
                  </span>
                </li>
              </FadeIn>
            ))}
          </ul>
        </div>
      </section>

      {/* Closing CTA */}
      <section
        className="section"
        style={{ backgroundColor: "#1E3A5F" }}
        data-testid="voor-wie-closing"
      >
        <div className="container-soveu max-w-3xl text-center text-white">
          <FadeIn>
            <h2 className="h2-display text-white mb-5">
              Past uw organisatie hierbij?
            </h2>
            <p className="body-lg text-white/75 mb-8">
              Of u nu bedrijf, adviesbureau, gemeente, woningcorporatie, onderwijsinstelling of zorgorganisatie bent — als AI-gebruik, AI Act readiness of digitale verantwoording op uw bordje ligt, bespreken wij graag waar uw organisatie nu staat en welke vervolgstap logisch is.
            </p>
            <Link
              to="/contact"
              className="btn-primary btn-on-navy"
              data-testid="voor-wie-final-cta"
            >
              Plan een gesprek
              <ArrowRight size={18} />
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
